import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { indexVerseSource, projectVerseCell, justifyVerseHalf, VerseFragment, VerseIndex } from '../../lib/ledgerVerseLayout'
import './LedgerVerseCell.css'
import { readVerseSelection, VerseTextSelection } from '../../lib/ledgerVerseSelection'
import { VerseWordAction } from '../../lib/ledgerVerseEditing'

type Props = { source: string; text: string; enabled: boolean; children: React.ReactNode; onTextSelection?: (selection: VerseTextSelection) => void;
  wordActions?: VerseWordAction[]; selectedBoundary?: number | null; onBoundarySelect?: (boundary: number) => void;
  marks?: Array<{ start: number; end: number; className: string }>
}

/** Source-offset-aware display. Decoration never reaches an editor callback. */
export function LedgerVerseCell({ source, text, enabled, children, ...interaction }: Props) {
  const index = useMemo(() => enabled ? indexVerseSource(source) : null, [source, enabled])
  const fragments = useMemo(() => projectVerseCell(index, text), [index, text])
  const allActionsVisible = !interaction.wordActions?.some(action => !fragments?.some(f =>
    f.cellSpans.some(span => action.start <= span.start && span.start < action.end)))
  return fragments && index && allActionsVisible
    ? <VerseReading fragments={fragments} index={index} literal={text} {...interaction} />
    : <>{children}</>
}

function VerseReading({ fragments, index, literal, onTextSelection, wordActions, selectedBoundary, onBoundarySelect, marks }: { fragments: VerseFragment[]; index: VerseIndex; literal: string } & Pick<Props, 'onTextSelection' | 'wordActions' | 'selectedBoundary' | 'onBoundarySelect' | 'marks'>) {
  const ref = useRef<HTMLDivElement>(null)
  const [layout, setLayout] = useState<{ width: number; size: number; font: string; metric: number } | null>(null)
  useEffect(() => {
    const root = ref.current
    if (!root) return
    const copy = (event: ClipboardEvent) => {
      const selection = window.getSelection()
      if (!selection?.rangeCount || selection.isCollapsed || !selection.getRangeAt(0).intersectsNode(root)) return
      // Cross-cell selections have no single cell-offset contract. Block native
      // decorative copying rather than inventing a combined source selection.
      event.preventDefault()
      const mapped = readVerseSelection(root, literal, selection)
      if (mapped) event.clipboardData?.setData('text/plain', mapped.selectedText)
    }
    document.addEventListener('copy', copy)
    return () => document.removeEventListener('copy', copy)
  }, [literal])
  useLayoutEffect(() => {
    const root = ref.current
    if (!root) return
    let disposed = false, frame = 0
    const update = () => {
      if (disposed || !root.clientWidth) return
      const ctx = document.createElement('canvas').getContext('2d')
      if (!ctx) return
      const style = getComputedStyle(root)
      const family = style.fontFamily
      const size = parseFloat(style.fontSize)
      ctx.font = `${size}px ${family}`
      // Witness-wide scale keeps fragments in different rows geometrically aligned.
      const longest = Math.max(...index.halves.map(h => ctx.measureText(h.text).width), 1)
      const metric = longest + ctx.measureText('ابتثجحخدذرزسشصضطظعغفقكلمنهوي').width
      const width = Math.max(Math.ceil(longest) + 2, (root.clientWidth - 28) / 2)
      setLayout(previous => previous?.width === width && previous?.size === size && previous?.font === family && previous?.metric === metric
        ? previous : { width, size, font: family, metric })
    }
    const schedule = () => { if (!disposed) { cancelAnimationFrame(frame); frame = requestAnimationFrame(update) } }
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
    observer?.observe(root)
    window.addEventListener('resize', schedule)
    document.fonts?.ready.then(schedule)
    update()
    return () => { disposed = true; observer?.disconnect(); window.removeEventListener('resize', schedule); cancelAnimationFrame(frame) }
  }, [index])
  const presented = useMemo(() => {
    const format = (f: VerseFragment, stretched: ReturnType<typeof justifyVerseHalf>) => {
      const a = stretched.offset(f.start), b = stretched.offset(f.end)
      const byOffset = new Map(f.cellSpans.map(span => [span.halfOffset, { start: span.start, end: span.end }]))
      return { ...f, before: stretched.text.slice(0, a), body: stretched.text.slice(a, b), after: stretched.text.slice(b), glyphMap: stretched.origins.slice(a, b).map(origin => byOffset.get(origin) || null) }
    }
    const plain = () => fragments.map(f => format(f, { text: f.half.text, offset: n => n, origins: Array.from({ length: f.half.text.length }, (_, i) => i) }))
    if (!layout) return plain()
    const ctx = document.createElement('canvas').getContext('2d')
    if (!ctx) return plain()
    ctx.font = `${layout.size}px ${layout.font}`
    return fragments.map(f => {
      const stretched = justifyVerseHalf(f.half.text, layout.width - 2, s => ctx.measureText(s).width)
      return format(f, stretched)
    })
  }, [fragments, layout])
  const verses = Array.from(new Set(fragments.map(f => f.half.verse)))
  const reportSelection = () => {
    const mapped = ref.current && readVerseSelection(ref.current, literal, window.getSelection())
    if (mapped) onTextSelection?.(mapped)
  }
  const renderBody = (f: typeof presented[number]) => {
    if (!wordActions?.length && !marks?.length) return <span data-verse-glyph-map={JSON.stringify(f.glyphMap)}>{f.body}</span>
    // Group by source word/mark. Never split a word for inserted elongation.
    let preceding = -1
    const runs: Array<{ start: number; end: number; action?: VerseWordAction; tone: string }> = []
    for (let i = 0; i < f.body.length; i++) {
      const span = f.glyphMap[i]
      if (span) preceding = span.start
      const offset = span?.start ?? (f.body[i] === '\u0640' ? preceding : -1)
      const action = wordActions?.find(a => a.start <= offset && offset < a.end)
      const tone = marks?.filter(m => m.start <= offset && offset < m.end).map(m => m.className).join(' ') || ''
      const last = runs[runs.length - 1]
      if (last && last.action === action && last.tone === tone) last.end = i + 1
      else runs.push({ start: i, end: i + 1, action, tone })
    }
    return runs.map(run => {
      const glyphs = <span data-verse-glyph-map={JSON.stringify(f.glyphMap.slice(run.start, run.end))}>{f.body.slice(run.start, run.end)}</span>
      return run.action ? <button key={run.start} type="button" aria-label={run.action.label}
        aria-pressed={selectedBoundary === run.action.boundary}
        className={`ledger-verse-word ${selectedBoundary === run.action.boundary ? 'text-blue-700 underline decoration-blue-600 decoration-2 underline-offset-[6px]' : ''} ${run.tone}`}
        onClick={event => { event.stopPropagation(); onBoundarySelect?.(run.action!.boundary) }}>{glyphs}</button>
        : <span key={run.start} className={run.tone || undefined}>{glyphs}</span>
    })
  }
  return <div ref={ref} className="ledger-verse-cell font-reader-arabic" data-testid="ledger-verse-cell">
    <span className="sr-only" aria-hidden={wordActions ? true : undefined}>{literal}</span>
    <div aria-hidden={wordActions ? undefined : true} className="ledger-verse-lines"
      onMouseUp={event => { event.stopPropagation(); reportSelection() }} onKeyUp={reportSelection} onTouchEnd={reportSelection}>
      {verses.map(verse => <div className="ledger-verse-pair" key={verse}>
        {([1, 2] as const).map(half => {
          const f = presented.find(p => p.half.verse === verse && p.half.half === half)
          return <div className={`ledger-verse-half ${half === 2 ? 'second' : ''}`} key={half} style={{ width: layout?.width }}>
            {f && <><span aria-hidden="true" className="ledger-verse-reserved">{f.before}</span><span className="ledger-verse-fragment">
              {f.start > 0 && <ContinuationArrow kind="arrival" />}{renderBody(f)}
              {f.end < f.half.text.length && <ContinuationArrow kind="departure" />}
            </span><span aria-hidden="true" className="ledger-verse-reserved">{f.after}</span></>}
          </div>
        })}
      </div>)}
    </div>
  </div>
}

function ContinuationArrow({ kind }: { kind: 'arrival' | 'departure' }) {
  return <svg aria-hidden="true" className={`ledger-verse-arrow ${kind}`} viewBox="0 0 14 12" focusable="false">
    <path d={kind === 'arrival' ? 'M11 1 V6 H2 M5 3 L2 6 L5 9' : 'M12 2 H5 V10 M2 7 L5 10 L8 7'} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
}
